/**
 * ESCHER FALLS — 接続グラフの構築と水の到達計画。
 *
 * MODULE-CONTRACTS.md §3 の実装。**純関数のみ。three.js に依存しない。**
 *
 * このモジュールが扱うグラフは**無向**である。ノードは {@link Port}、辺は
 * 「同一ピース内のパイプ（{@link Segment}）」と「錯視接続（{@link Link}）」の2種類。
 * リンクは継ぎ目であってパイプではないので、渡るのに時間もマス数もかからない。
 */

import {
  FILL_MS_PER_CELL,
  SPILL_DURATION_MS,
  type ConnectionGraph,
  type FlowPlan,
  type FlowStep,
  type GraphEdge,
  type Link,
  type Piece,
  type Port,
  type PortId,
  type Segment,
  type SegmentId,
  type SpillEvent,
  type ViewRelation,
} from './types';
import { collectPorts, findDrainPorts, findGoalPorts, findSourcePort } from './pieces';
import { overlapsInRelation } from './projection';

/** 文字列の辞書順比較（`localeCompare` はロケール依存で決定性がないので使わない）。 */
function compareIds(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

// =============================================================================
// buildGraph
// =============================================================================

/**
 * 指定した**接続関係**におけるグラフを構築する。副作用なし。
 *
 * 引数は姿勢ではなく関係であることに注意。真上の4姿勢は同一の関係に落ちる。
 *
 * **この関数を回転補間の途中フレームで呼んではならない**
 * （PROJECTION-VERIFIED §6：補間中の角度では数値的に偽の接続が成立する）。
 * 呼び出しは `rotating → idle` の瞬間だけである。
 */
export function buildGraph(pieces: readonly Piece[], relation: ViewRelation): ConnectionGraph {
  const portList: readonly Port[] = collectPorts(pieces);

  const ports = new Map<PortId, Port>();
  for (const port of portList) ports.set(port.id, port);

  const segments = new Map<SegmentId, Segment>();
  for (const piece of pieces) {
    for (const segment of piece.segments) segments.set(segment.id, segment);
  }

  // ポート → そのポートを端点に持つセグメント（仕様上、1ポートにつき高々1本）。
  const segmentByPort = new Map<PortId, Segment>();
  for (const segment of segments.values()) {
    segmentByPort.set(segment.fromPort, segment);
    segmentByPort.set(segment.toPort, segment);
  }

  // 錯視リンク：全ポート対の素朴な総当たり O(N²)。N は高々 40 程度なので最適化しない
  // （ハッシュによる高速化は反復順が実装依存になり、決定性の保証が難しくなる）。
  const links: Link[] = [];
  for (let i = 0; i < portList.length; i += 1) {
    for (let j = i + 1; j < portList.length; j += 1) {
      const pa = portList[i];
      const pb = portList[j];
      // 同一ピースの2ポートは Segment で既に繋がっている。錯視リンクは張らない
      // （CORE-SPEC §1.6。張ると L 字が自分自身と短絡して全レベルが自明に解ける）。
      if (pa.pieceId === pb.pieceId) continue;
      if (!overlapsInRelation(pa.pos, pb.pos, relation)) continue;
      const a = pa.id < pb.id ? pa.id : pb.id;
      const b = pa.id < pb.id ? pb.id : pa.id;
      links.push({ id: `${a}|${b}`, a, b });
    }
  }
  links.sort((x, y) => compareIds(x.id, y.id));

  // 隣接リスト：全ポートをキーに持つ（孤立ポートも空配列で存在する）。
  // 並びは「segment 辺 → link 辺（linkId 昇順）」で固定する。
  const adjacency = new Map<PortId, GraphEdge[]>();
  for (const port of portList) {
    const edges: GraphEdge[] = [];
    const segment = segmentByPort.get(port.id);
    if (segment !== undefined) {
      edges.push({
        kind: 'segment',
        segmentId: segment.id,
        to: segment.fromPort === port.id ? segment.toPort : segment.fromPort,
      });
    }
    adjacency.set(port.id, edges);
  }
  // links は id 昇順にソート済みなので、この順に push すれば link 辺も id 昇順に並ぶ。
  for (const link of links) {
    adjacency.get(link.a)?.push({ kind: 'link', linkId: link.id, to: link.b });
    adjacency.get(link.b)?.push({ kind: 'link', linkId: link.id, to: link.a });
  }

  return { relation, ports, segments, links, adjacency };
}

// =============================================================================
// isReachable / isSolvable
// =============================================================================

/** from から to へ（無向・時間や budget を無視して）到達可能か。 */
export function isReachable(graph: ConnectionGraph, from: PortId, to: PortId): boolean {
  if (!graph.ports.has(from)) return false;
  if (from === to) return true;

  const visited = new Set<PortId>([from]);
  const queue: PortId[] = [from];
  for (let head = 0; head < queue.length; head += 1) {
    const edges = graph.adjacency.get(queue[head]);
    if (edges === undefined) continue;
    for (const edge of edges) {
      if (visited.has(edge.to)) continue;
      if (edge.to === to) return true;
      visited.add(edge.to);
      queue.push(edge.to);
    }
  }
  return false;
}

/** pieces をその関係で組んだとき、水源→ゴールが到達可能か（budget 無視）。 */
export function isSolvable(pieces: readonly Piece[], relation: ViewRelation): boolean {
  const source = findSourcePort(pieces);
  const goals = findGoalPorts(pieces);
  if (source === null || goals.length === 0) return false;
  const graph = buildGraph(pieces, relation);
  // **全てのゴールに届いて初めて解けたとみなす。**
  if (!goals.every((g) => isReachable(graph, source.id, g.id))) return false;
  // **涸れ井戸に1つでも届いたら解ではない。**
  // これまでの条件はすべて「繋げる」方向だったが、これは初めての負の条件で、
  // 視点を選ぶのではなく**除外する**読みを要求する。
  return findDrainPorts(pieces).every((d) => !isReachable(graph, source.id, d.id));
}

// =============================================================================
// planFlow
// =============================================================================

const EMPTY_PLAN: FlowPlan = {
  steps: [],
  reachesGoal: false,
  goalTime: null,
  totalCells: 0,
  durationMs: 0,
  spills: [],
  budgetExceeded: false,
  drained: false,
};

/** 探索キューの要素。`seq` は同時刻のタイブレーク（＝決定性）のためだけに存在する。 */
interface QueueEntry {
  readonly time: number;
  readonly seq: number;
  readonly port: PortId;
}

/** 1つの時刻レイヤで充填候補になるセグメント。`from` が水の入る側のポート。 */
interface FillCandidate {
  readonly segmentId: SegmentId;
  readonly from: PortId;
  readonly to: PortId;
}

/**
 * 放水の充填計画を作る。`waterBudget` 省略/null で無制限。
 *
 * 水は流体なので「到達できる場所すべて」に広がる（プレイヤーが経路を選ぶのではない）。
 * したがって時刻をコストとする一様コスト探索（ダイクストラ）で全域を舐める。
 * リンクはコスト 0 なので、同じ継ぎ目に集まる複数の分岐は**同じ開始時刻**を持つ。
 *
 * ### 同時刻に複数の到達が成立する場合の扱い（決定性の要）
 *
 * 探索は「到達時刻が等しいポートの集まり」を1枚のレイヤとして扱い、
 * レイヤ内では必ず次の順に処理する。
 *
 * 1. **0 コストのリンクを閉包する。** その時刻に水が届くポートを全部確定させる。
 * 2. **ゴール判定。** 1 の結果だけで決まるので、どのポートを先に取り出したか
 *    （＝リンク ID ひいてはピース ID の辞書順）には左右されない。
 * 3. **セグメントの充填＝ budget の消費。** 候補は `segmentId` 昇順の正準順で処理する。
 *
 * 3 を 1・2 より**後**に置くのが要点である。逆順だと「予算に入りきらない管に
 * 先に当たったせいで、同時刻に継ぎ目1つ隣で成立していたゴール到達が消える」という、
 * ピース ID の辞書順しだいで成否が反転する挙動になる。
 * MODULE-CONTRACTS §3.2 の「`waterBudget = 0` でもリンクだけで届くなら
 * `reachesGoal` は true になりうる」は、この順序でしか満たせない。
 *
 * ### budget が尽きたときの打ち切り方
 *
 * 入りきらないセグメントに当たった時点で放水そのものが止まり、
 * **それ以降どの管にも新しく水は入らない**（`budgetExceeded = true`）。
 * ただし**すでに水が入った管の中の水は流れ続ける**ので、探索は
 * 充填済みセグメントとリンクの上を最後まで伝播させる。
 * 探索ごと打ち切ると、`steps` は「水がゴールに注ぎ込む絵」を最後まで再生するのに
 * `reachesGoal` だけが false という、プランと演出が食い違う状態になる
 * （プレイヤーには「入ったのに失敗」と映る）。**水が届くなら clear が優先である。**
 */
export function planFlow(
  graph: ConnectionGraph,
  sourcePortId: PortId,
  goalPortIds: readonly PortId[],
  waterBudget?: number | null,
  /** 涸れ井戸のポート。ここへ水が届いたら `drained` が立つ。 */
  drainPortIds: readonly PortId[] = [],
): FlowPlan {
  if (!graph.ports.has(sourcePortId)) return EMPTY_PLAN;

  const budget = waterBudget ?? null;

  const arrival = new Map<PortId, number>();
  const settled = new Set<PortId>();
  const filled = new Set<SegmentId>();
  const steps: FlowStep[] = [];
  const queue: QueueEntry[] = [];
  let usedCells = 0;
  // 各ゴールに水が届いた時刻。**全部埋まって初めて到達**とみなす。
  const goalTimes = new Map<PortId, number>();
  let budgetExceeded = false;
  let seq = 0;

  const relax = (to: PortId, time: number): void => {
    if (settled.has(to)) return;
    const known = arrival.get(to);
    if (known !== undefined && known <= time) return;
    arrival.set(to, time);
    queue.push({ time, seq, port: to });
    seq += 1;
  };

  arrival.set(sourcePortId, 0);
  queue.push({ time: 0, seq, port: sourcePortId });
  seq += 1;

  while (queue.length > 0) {
    // このレイヤの時刻＝キューに残っている最小の到達時刻。
    // ポート数が高々 40 程度なので線形探索でよい（ヒープ不要）。
    let layerTime = Infinity;
    for (const entry of queue) {
      if (entry.time < layerTime) layerTime = entry.time;
    }

    // --- 第1相: 時刻 layerTime に届くポートを、0 コストのリンクで閉包する ---
    const layer: PortId[] = [];
    for (;;) {
      let best = -1;
      for (let i = 0; i < queue.length; i += 1) {
        if (queue[i].time !== layerTime) continue;
        if (best === -1 || queue[i].seq < queue[best].seq) best = i;
      }
      if (best === -1) break;
      const entry = queue.splice(best, 1)[0];
      if (settled.has(entry.port)) continue; // 遅延削除：改善済みの古いエントリ
      settled.add(entry.port);
      layer.push(entry.port);

      const edges = graph.adjacency.get(entry.port);
      if (edges === undefined) continue;
      for (const edge of edges) {
        // 継ぎ目を渡るのは時間 0。関係するポートはこのレイヤに合流する。
        if (edge.kind === 'link') relax(edge.to, layerTime);
      }
    }

    // --- 第2相: ゴール判定。budget を1マスも消費する前に行う ---
    for (const gid of goalPortIds) {
      if (!goalTimes.has(gid) && settled.has(gid)) goalTimes.set(gid, layerTime);
    }

    // --- 第3相: このレイヤから伸びるセグメントを充填する ---
    // 予算が尽きた後は新しい管に水は入らない。ただし探索は続ける
    // （充填済みの管の中の水は流れ続け、その先のゴール到達は生きている）。
    if (budgetExceeded) continue;

    const candidates: FillCandidate[] = [];
    for (const port of layer) {
      const edges = graph.adjacency.get(port);
      if (edges === undefined) continue;
      for (const edge of edges) {
        if (edge.kind === 'segment') {
          candidates.push({ segmentId: edge.segmentId, from: port, to: edge.to });
        }
      }
    }
    // 充填順は「どのポートを先に取り出したか」ではなく segmentId で決める。
    // 両端が同時に濡れるセグメントもあるので、同点は入口側のポート ID で割る。
    candidates.sort(
      (x, y) => compareIds(x.segmentId, y.segmentId) || compareIds(x.from, y.from),
    );

    for (const cand of candidates) {
      if (filled.has(cand.segmentId)) continue; // 閉路でも同じ管を二度満たさない
      const segment = graph.segments.get(cand.segmentId);
      if (segment === undefined) continue;
      if (budget !== null && usedCells + segment.cells > budget) {
        // 入りきらない管に当たった時点で放水そのものが止まる。
        budgetExceeded = true;
        break;
      }
      filled.add(segment.id);
      usedCells += segment.cells;
      const duration = segment.cells * FILL_MS_PER_CELL;
      steps.push({
        segmentId: segment.id,
        startTime: layerTime,
        duration,
        fromPort: cand.from,
        toPort: cand.to,
        reversed: cand.from !== segment.fromPort,
      });
      relax(cand.to, layerTime + duration);
    }
  }

  // こぼれ：水が届いたポートのうち、ゴールでなく、リンク辺を1本も持たないもの。
  // 「どこにも繋がっていない開口部」という物理的な定義なので探索順に依存しない。
  const spills: SpillEvent[] = [];
  for (const portId of settled) {
    if (goalPortIds.includes(portId)) continue;
    const edges = graph.adjacency.get(portId);
    if (edges !== undefined && edges.some((e) => e.kind === 'link')) continue;
    spills.push({ portId, time: arrival.get(portId) ?? 0 });
  }
  spills.sort((a, b) => a.time - b.time || compareIds(a.portId, b.portId));

  steps.sort((a, b) => a.startTime - b.startTime || compareIds(a.segmentId, b.segmentId));

  let durationMs = 0;
  for (const step of steps) durationMs = Math.max(durationMs, step.startTime + step.duration);
  for (const spill of spills) durationMs = Math.max(durationMs, spill.time + SPILL_DURATION_MS);

  // 全ゴールに届いたときだけ成功。時刻は「最後の1つが埋まった瞬間」。
  const allReached = goalPortIds.length > 0 && goalPortIds.every((g) => goalTimes.has(g));
  const goalTime = allReached ? Math.max(...goalPortIds.map((g) => goalTimes.get(g) ?? 0)) : null;

  return {
    steps,
    reachesGoal: allReached,
    goalTime,
    totalCells: usedCells,
    durationMs,
    spills,
    budgetExceeded,
    drained: drainPortIds.some((id) => arrival.has(id)),
  };
}
